import {isSubstring} from "@prosay/common"
import {TextInput} from "@prosay/components"
import {translate} from "@prosay/i18n"
import {getLogger} from "@prosay/logging"
import React, {useMemo} from "react"
import {FlatList, ScrollView, StyleSheet, View} from "react-native"
import {Badge, Button, List} from "react-native-paper"
import {Actions} from "react-native-router-flux"
import {FAQHeader, FAQHeaderWithSearch, Field, Form} from "../../components"
import {
    ConversationTopicInfoFragment,
    GetConversationsDocument,
    GetConversationsQuery,
    GetConversationsQueryVariables,
    useCreateConversationMutation,
    useGetTopicCategoriesQuery,
} from "../../graphql"
import {TopicSearchContainer} from "../../state"
import {readQuery} from "../../util"

const logger = getLogger(__filename)

const styles = StyleSheet.create({
    categoryIconBadgeStyle: {
        left: -25,
        marginRight: -20,
        position: "relative",
    },
    pageContainer: {
        flex: 1,
    },

    submitButton: {
        marginBottom: 10,
        width: "100%",
    },
})

export interface SelectTopicAndCategoryProps {}
export const SelectTopicAndCategory: React.FC<SelectTopicAndCategoryProps> & {
    navigationOptions: {
        header: React.ReactNode
    }
} = () => {
    const [search] = TopicSearchContainer.useContainer()
    const {data, loading, refetch} = useGetTopicCategoriesQuery()

    const categories = useMemo(
        () =>
            (data?.getTopicCategories ?? []).filter(
                category =>
                    isSubstring(category.name, search) ||
                    category.topics?.some(topic =>
                        isSubstring(topic.name, search),
                    ),
            ),
        [data, search],
    )

    return (
        <View style={styles.pageContainer}>
            <List.Section style={styles.pageContainer}>
                <List.Subheader>
                    {translate(
                        "ui.screen.createConversation.selectTopicCategory.header",
                    )}
                </List.Subheader>

                <FlatList
                    refreshing={loading}
                    onRefresh={refetch}
                    data={categories}
                    keyExtractor={({id}) => id}
                    renderItem={({item: category}) => (
                        <List.Accordion
                            title={category.name}
                            left={props => (
                                <>
                                    <List.Icon
                                        icon={category.icon ?? "script"}
                                        {...props}
                                    />
                                    <Badge
                                        style={styles.categoryIconBadgeStyle}
                                        visible={!!category.topics?.length}>
                                        {category.topics?.length ?? 0}
                                    </Badge>
                                </>
                            )}>
                            <FlatList
                                refreshing={false}
                                data={category.topics}
                                keyExtractor={({id}) => id}
                                renderItem={({item: topic}) => (
                                    <List.Item
                                        title={topic.name}
                                        description={topic.description}
                                        onPress={() =>
                                            Actions.writeDescription({
                                                topic: {...topic, category},
                                            })
                                        }
                                        left={props => (
                                            <List.Icon
                                                icon={topic.icon ?? "script"}
                                                {...props}
                                            />
                                        )}
                                        right={props => (
                                            <List.Icon
                                                icon="chevron-right"
                                                {...props}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </List.Accordion>
                    )}
                />
            </List.Section>
        </View>
    )
}
SelectTopicAndCategory.navigationOptions = {
    header: (
        <FAQHeaderWithSearch
            title={translate("ui.navigation.tabBar.createConversation")}
            useStateHook={TopicSearchContainer.useContainer}
        />
    ),
}

export interface WriteDescriptionProps {
    question?: string
    topic: ConversationTopicInfoFragment
}
export const WriteDescription: React.FC<WriteDescriptionProps> & {
    navigationOptions: {
        header: React.ReactNode
    }
} = ({question = "", topic: {category, ...topic}}) => {
    const mutation = useCreateConversationMutation({
        onCompleted: data => {
            if (!data.createConversation) {
                logger.warn`createConversation mutation returned empty data while calling onCompleted!`
                return
            }

            Actions.pop()
            Actions.conversations()
            Actions.messages({
                id: data.createConversation.id,
                title: data.createConversation.case.title,
            })
        },
        update: (cache, {data, errors}) => {
            if (errors || !data?.createConversation) {
                return
            }

            const existing = readQuery<
                GetConversationsQuery,
                GetConversationsQueryVariables
            >(cache, {
                query: GetConversationsDocument,
            })
            cache.writeQuery<
                GetConversationsQuery,
                GetConversationsQueryVariables
            >({
                data: {
                    __typename: "Query",
                    getConversations: [
                        data.createConversation,
                        ...(existing?.getConversations ?? []),
                    ],
                },
                query: GetConversationsDocument,
            })
        },
    })

    // TODO: Extract out all the Appbar.Header business into its own wrappre component
    return (
        <View style={styles.pageContainer}>
            <Form
                mutation={mutation}
                initialValues={{title: question, topicId: topic.id}}>
                {({submitForm}) => (
                    <ScrollView style={styles.pageContainer}>
                        <List.Section>
                            <List.Subheader>
                                {translate(
                                    "ui.screen.createConversation.writeDescription.topicHeader",
                                )}
                            </List.Subheader>

                            <List.Item
                                title={category.name}
                                description={category.description}
                                left={props => (
                                    <List.Icon
                                        icon={category.icon ?? "script"}
                                        {...props}
                                    />
                                )}
                            />

                            <List.Item
                                title={topic.name}
                                description={topic.description}
                                style={{marginLeft: 25}}
                                left={props => (
                                    <List.Icon
                                        icon={topic.icon ?? "script"}
                                        {...props}
                                    />
                                )}
                            />
                        </List.Section>

                        <List.Section>
                            <List.Subheader>
                                {translate(
                                    "ui.screen.createConversation.writeDescription.descriptionHeader",
                                )}
                            </List.Subheader>

                            <Field mutation={mutation} name="title">
                                {({
                                    field: {error, onBlur, onChangeText, value},
                                }) => (
                                    <TextInput
                                        mode="outlined"
                                        placeholder={translate(
                                            "ui.screen.createConversation.writeDescription.descriptionPlaceholder",
                                        )}
                                        multiline
                                        numberOfLines={10}
                                        {...{
                                            error,
                                            onBlur,
                                            onChangeText,
                                            value,
                                        }}
                                    />
                                )}
                            </Field>
                        </List.Section>

                        <Button
                            style={styles.submitButton}
                            onPress={submitForm}>
                            {translate(
                                "ui.screen.createConversation.writeDescription.submitButton",
                            )}
                        </Button>
                    </ScrollView>
                )}
            </Form>
        </View>
    )
}
WriteDescription.navigationOptions = {
    header: (
        <FAQHeader
            title={translate("ui.navigation.tabBar.createConversation")}
            backButton={Actions.pop}
        />
    ),
}
